import React from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { getFormattedDateAndTime, useAllSubpersons, useStateWithDebounce } from '@modmd/data'
import DashboardPage from 'internal-portal/sharedComponents/DashboardPage'
import Datalist from 'components/Datalist'
import Card from 'components/Card'
import { Box, Container } from 'components/Layout'
import TextInput from 'components/TextInput'
import Icon from 'components/Icon'
import { DEVICE } from 'theme'
import { ROUTES } from 'internal-portal/constants/routes'
import Pagination from 'components/Pagination'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { useSearchState } from 'internal-portal/utils/useSearchState'

const SKELETON_COLUMNS = 3
export const USERS_PAGE_LENGTH = 10

const SubpersonsList: React.VFC<RouteComponentProps> = ({ history }) => {
  const searchState = useSearchState<{
    nameLike?: string
    parentNameLike?: string
    page?: string
  }>()
  const [nameLike, setNameLike] = useStateWithDebounce(searchState.data.nameLike, (nameLike) => {
    searchState.setSearchState({ nameLike, page: 1 }, false, true)
  })
  const [parentNameLike, setParentNameLike] = useStateWithDebounce(
    searchState.data.parentNameLike,
    (parentNameLike) => {
      searchState.setSearchState({ parentNameLike, page: 1 }, false, true)
    }
  )
  const { data: subpersons, loading: subpersonsLoading } = useAllSubpersons({
    filter: {
      emailOrNameLike: searchState.data.nameLike,
      parentEmailOrNameLike: searchState.data.parentNameLike,
    },
    page: Number(searchState.data.page) || 1,
    pageLength: 10,
  })
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const userCount = subpersons?.users?.pagination.total ?? 0
  const pageLength = subpersons?.users?.pagination.length ?? 0

  return (
    <DashboardPage>
      <Container>
        <Card>
          <Datalist
            isLoading={subpersonsLoading}
            skeletonProps={{
              columns: SKELETON_COLUMNS,
              rows: USERS_PAGE_LENGTH,
            }}
            data={subpersons?.users.users || []}
            header={
              <Datalist.Title
                rightContent={
                  <Box display="flex" gridGap="1rem">
                    <TextInput
                      name="nameLike"
                      placeholder="Name"
                      value={nameLike}
                      onChange={(event) => {
                        setNameLike(event.target.value)
                      }}
                      leftIcon={<Icon.Search />}
                      hasBottomContent={false}
                    />
                    <TextInput
                      name="parentNameLike"
                      placeholder="Parent Name"
                      value={parentNameLike}
                      onChange={(event) => {
                        setParentNameLike(event.target.value)
                      }}
                      leftIcon={<Icon.Search />}
                      hasBottomContent={false}
                    />
                  </Box>
                }
              >
                Subpersons
              </Datalist.Title>
            }
            shouldRenderTableHeader={isDesktop}
            showActionIndicators
            renderTableHeader={() => (
              <Datalist.Row gridTemplateColumns="repeat(3, 1fr)">
                <Datalist.HeaderCell label="Name" />
                <Datalist.HeaderCell label="Parent Name" />
                <Datalist.HeaderCell label="Birthdate" />
              </Datalist.Row>
            )}
            renderTableRow={(subperson) => {
              const birthDate = getFormattedDateAndTime(subperson.birthDate)
              const name = `${subperson?.firstName || ''} ${subperson?.lastName || ''}`
              const parent = `${subperson?.ParentUser?.firstName || ''} ${
                subperson.ParentUser?.lastName || ''
              }`
              return (
                <Datalist.Row
                  gridTemplateColumns="repeat(3, 1fr)"
                  onClick={() => history.push(`/${ROUTES.USERS}/${Number(subperson.id)}`)}
                >
                  <Datalist.Cell label="Name">{name}</Datalist.Cell>
                  <Datalist.Cell label="Parent Name">{parent}</Datalist.Cell>
                  <Datalist.Cell label="Birthdate">{birthDate}</Datalist.Cell>
                </Datalist.Row>
              )
            }}
          />
        </Card>
        {userCount > pageLength && (
          <Pagination.Wrapper>
            <Pagination
              firstPage={1}
              lastPage={Math.ceil(userCount / pageLength)}
              currentPage={Math.ceil((subpersons?.users?.pagination.from || 0) / pageLength) + 1}
              onPageChange={(pageNumber) => {
                searchState.setSearchState({ page: pageNumber }, false)
              }}
            />
          </Pagination.Wrapper>
        )}
      </Container>
    </DashboardPage>
  )
}

export { SubpersonsList }
